import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { Music, ListMusic, Mic, Clock, Lock, ChevronRight, Calendar, ChevronDown, Volume2, VolumeX } from 'lucide-react';
import { usePlayer } from '../context/PlayerContext';
import { useBusiness } from '../context/BusinessContext';
import { useTheme } from '../context/ThemeContext';
import { Slider } from '@/components/ui/slider';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS_OF_WEEK = [
  { key: 'sunday', label: 'Sunday' },
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
];

const Dashboard = () => {
  const { 
    startAutoPlaylist, 
    currentIndex, 
    isAutoMode, 
    currentPlaylist,
    isPlaying,
    currentSong,
    stopAutoMode,
    volume,
    updateVolume,
    isMuted,
    toggleMute
  } = usePlayer();
  
  const { selectedBusinessId, selectedBusiness } = useBusiness();
  const { isLight } = useTheme();
  
  const [loading, setLoading] = useState(true);
  const [playlists, setPlaylists] = useState([]);
  const [songs, setSongs] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [fullDaySchedule, setFullDaySchedule] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [currentPlayingItem, setCurrentPlayingItem] = useState(null);
  
  // Track if we've started auto-play for current schedule
  const hasStartedRef = useRef(false);
  const currentScheduleIdRef = useRef(null);
  const currentBusinessIdRef = useRef(null);
  const lastLoggedItemRef = useRef(null);
  const isBusinessChangingRef = useRef(false);
  
  // Track actual playback time (considering mute state)
  const playbackStartTimeRef = useRef(null);
  const totalUnmutedTimeRef = useRef(0);
  const lastMuteStateRef = useRef(false);
  const currentTrackingItemRef = useRef(null);
  
  // Ref for auto-scrolling to current playing item
  const currentPlayingRef = useRef(null);

  // Log playback event
  // Log playback with actual duration (time played when not muted)
  const logPlayback = useCallback(async (item, actualDuration) => {
    if (!item || actualDuration <= 0) return;
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('item_type', item.type);
      formData.append('item_id', item.id);
      formData.append('item_name', item.name);
      formData.append('duration', item.duration || 0);
      formData.append('actual_duration', Math.round(actualDuration));
      if (selectedBusinessId) {
        formData.append('business_id', selectedBusinessId);
      }
      
      await axios.post(`${API}/playback/log`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Failed to log playback:', error);
    }
  }, [selectedBusinessId]);

  // Track mute state changes to calculate actual playback time
  useEffect(() => {
    if (!isPlaying || !currentTrackingItemRef.current) return;
    
    const now = Date.now();
    
    if (isMuted && !lastMuteStateRef.current) {
      // Just muted - add time since last unmute
      if (playbackStartTimeRef.current) {
        totalUnmutedTimeRef.current += (now - playbackStartTimeRef.current) / 1000;
      }
      playbackStartTimeRef.current = null;
    } else if (!isMuted && lastMuteStateRef.current) {
      // Just unmuted - start tracking
      playbackStartTimeRef.current = now;
    }
    
    lastMuteStateRef.current = isMuted;
  }, [isMuted, isPlaying]);

  // Log playback when song changes or stops
  const finalizePlaybackLog = useCallback(() => {
    if (!currentTrackingItemRef.current) return;
    
    let totalTime = totalUnmutedTimeRef.current;
    
    // Add current segment if not muted
    if (playbackStartTimeRef.current && !lastMuteStateRef.current) {
      totalTime += (Date.now() - playbackStartTimeRef.current) / 1000;
    }
    
    // Log if we have actual playtime (minimum 1 second)
    if (totalTime >= 1) {
      logPlayback(currentTrackingItemRef.current, totalTime);
    }
    
    // Reset tracking
    currentTrackingItemRef.current = null;
    playbackStartTimeRef.current = null;
    totalUnmutedTimeRef.current = 0;
  }, [logPlayback]);

  // Periodic logging - log every 15 seconds of unmuted playback
  useEffect(() => {
    // Don't set up interval if not playing or muted
    if (!isPlaying || isMuted) return;
    
    const interval = setInterval(() => {
      // Skip if no item being tracked
      if (!currentTrackingItemRef.current) return;
      
      // Calculate current unmuted time
      let currentUnmutedTime = totalUnmutedTimeRef.current;
      if (playbackStartTimeRef.current && !lastMuteStateRef.current) {
        currentUnmutedTime += (Date.now() - playbackStartTimeRef.current) / 1000;
      }
      
      // Log if we have at least 10 seconds of playback (lowered threshold)
      if (currentUnmutedTime >= 10) {
        // Log directly without clearing the tracking ref
        logPlayback(currentTrackingItemRef.current, currentUnmutedTime);
        // Just reset the timers, keep tracking the same item
        totalUnmutedTimeRef.current = 0;
        playbackStartTimeRef.current = Date.now();
      }
    }, 10000); // Check every 10 seconds
    
    return () => clearInterval(interval);
  }, [isPlaying, isMuted, logPlayback]);

  // Get current day key
  const getCurrentDayKey = useCallback(() => {
    const dayIndex = currentTime.getDay();
    return DAYS_OF_WEEK[dayIndex].key;
  }, [currentTime]);

  const getCurrentDayLabel = useCallback(() => {
    const dayIndex = currentTime.getDay();
    return DAYS_OF_WEEK[dayIndex].label;
  }, [currentTime]);

  // Auto-scroll to currently playing item when it changes
  useEffect(() => {
    if (currentPlayingRef.current) {
      currentPlayingRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [currentPlayingItem?.itemIndex]);

  // Update current time every second for real-time display
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Periodic data refresh every 30 seconds to catch new schedules
  useEffect(() => {
    const refreshTimer = setInterval(() => {
      const fetchLatest = async () => {
        try {
          const token = localStorage.getItem('token');
          const headers = { Authorization: `Bearer ${token}` };
          const businessQuery = selectedBusinessId ? `?business_id=${selectedBusinessId}` : '';
          
          const playlistsRes = await axios.get(`${API}/playlists${businessQuery}`, { headers });
          setPlaylists(playlistsRes.data);
        } catch (error) {
          console.error('Refresh error:', error);
        }
      };
      fetchLatest();
    }, 30000); // Refresh every 30 seconds
    
    return () => clearInterval(refreshTimer);
  }, [selectedBusinessId]);

  // Reset playback state ONLY when business actually changes (not on initial mount or page navigation)
  useEffect(() => {
    // Skip reset on initial mount - only reset when business actually changes
    if (currentBusinessIdRef.current === null) {
      currentBusinessIdRef.current = selectedBusinessId;
      return;
    }
    
    // Only reset if business actually changed
    if (currentBusinessIdRef.current !== selectedBusinessId) {
      // Set flag to prevent playback during business change
      isBusinessChangingRef.current = true;
      
      // Log last item's actual duration before switching
      finalizePlaybackLog();
      
      // Stop current playback immediately
      stopAutoMode();
      hasStartedRef.current = false;
      currentScheduleIdRef.current = null;
      lastLoggedItemRef.current = null;
      setCurrentPlayingItem(null);
      setFullDaySchedule([]);
      setPlaylists([]);
      setAnnouncements([]);
      currentBusinessIdRef.current = selectedBusinessId;
    }
  }, [selectedBusinessId, stopAutoMode, finalizePlaybackLog]);

  // Fetch data on mount and when business changes
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        // Build query params - playlists and announcements are business-scoped
        const businessQuery = selectedBusinessId ? `?business_id=${selectedBusinessId}` : '';

        const [songsRes, playlistsRes, announcementsRes] = await Promise.all([
          axios.get(`${API}/songs`, { headers }),  // Songs are shared
          axios.get(`${API}/playlists${businessQuery}`, { headers }),
          axios.get(`${API}/announcements${businessQuery}`, { headers })
        ]);

        setSongs(songsRes.data);
        setAnnouncements(announcementsRes.data);
        setPlaylists(playlistsRes.data);
        
        // Reset the started flag to allow new playback
        hasStartedRef.current = false;
        currentScheduleIdRef.current = null;
        
        // Clear the business changing flag after data is loaded
        isBusinessChangingRef.current = false;
      } catch (error) {
        console.error('Error fetching data:', error);
        isBusinessChangingRef.current = false;
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedBusinessId]);

  // Calculate full day schedule with exact song times - ONLY for current day
  const calculateFullDaySchedule = useCallback(() => {
    if (playlists.length === 0) return [];

    const schedule = [];
    const currentDayKey = getCurrentDayKey();

    for (const playlist of playlists) {
      if (!playlist.time_slots || playlist.time_slots.length === 0) continue;
      if (!playlist.items || playlist.items.length === 0) continue;

      for (const slot of playlist.time_slots) {
        // Filter by current day - support legacy data without day_of_week
        const slotDay = slot.day_of_week || 'monday';
        if (slotDay !== currentDayKey) continue;
        
        const [startHour, startMin] = slot.start_time.split(':').map(Number);
        const [endHour, endMin] = slot.end_time.split(':').map(Number);
        
        const slotStartSeconds = startHour * 3600 + startMin * 60;
        const slotEndSeconds = endHour * 3600 + endMin * 60;
        const slotDuration = slotEndSeconds - slotStartSeconds;

        // Get playlist items with their durations
        // Priority: item's stored duration > fetched entity's duration > default
        const playlistItems = playlist.items.map(item => {
          if (item.type === 'song') {
            const song = songs.find(s => s.id === item.id);
            return { 
              ...item,
              ...(song || {}),
              type: 'song',
              name: song?.name || item.name || 'Unknown Song',
              artist: song?.artist || item.artist || '',
              genre: item.genre || song?.genre || null,
              duration: item.duration || song?.duration || 180,
              file_path: song ? `${BACKEND_URL}${song.file_path}` : null
            };
          } else if (item.type === 'announcement') {
            const announcement = announcements.find(a => a.id === item.id);
            return { 
              ...item,
              ...(announcement || {}),
              type: 'announcement', 
              name: announcement?.name || item.name || 'Unknown Announcement',
              genre: null, // Announcements don't have genre
              duration: item.duration || announcement?.duration || 30,
              file_path: announcement ? `${BACKEND_URL}${announcement.file_path}` : null
            };
          }
          return null;
        }).filter(Boolean);

        if (playlistItems.length === 0) continue;

        // Calculate total playlist duration
        const playlistDuration = playlistItems.reduce((acc, item) => acc + item.duration, 0);
        
        // Calculate how many times playlist loops and generate schedule
        const scheduledItems = [];
        let currentTimeOffset = 0;
        let loopCount = 0;

        while (currentTimeOffset < slotDuration) {
          for (const item of playlistItems) {
            if (currentTimeOffset >= slotDuration) break;
            
            const itemStartSeconds = slotStartSeconds + currentTimeOffset;
            const itemEndSeconds = Math.min(itemStartSeconds + item.duration, slotEndSeconds);
            
            scheduledItems.push({
              ...item,
              startTimeSeconds: itemStartSeconds,
              endTimeSeconds: itemEndSeconds,
              startTimeFormatted: formatSecondsToTime(itemStartSeconds),
              endTimeFormatted: formatSecondsToTime(itemEndSeconds),
              loopNumber: loopCount + 1
            });
            
            currentTimeOffset += item.duration;
          }
          loopCount++;
        }

        schedule.push({
          playlistId: playlist.id,
          playlistName: playlist.name,
          slotStart: slot.start_time,
          slotEnd: slot.end_time,
          slotStartSeconds,
          slotEndSeconds,
          items: scheduledItems,
          totalLoops: loopCount,
          playlistItems // Original items for playback
        });
      }
    }

    // Sort by start time
    schedule.sort((a, b) => a.slotStartSeconds - b.slotStartSeconds);
    return schedule;
  }, [playlists, songs, announcements, getCurrentDayKey]);

  // Update schedule when data changes
  useEffect(() => {
    const schedule = calculateFullDaySchedule();
    setFullDaySchedule(schedule);
  }, [calculateFullDaySchedule]);

  // Find and play current scheduled item
  useEffect(() => {
    // Exit early if business is changing or no schedule
    if (isBusinessChangingRef.current || fullDaySchedule.length === 0) {
      setCurrentPlayingItem(null);
      return;
    }

    const now = currentTime;
    const currentSeconds = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();

    // Find current slot and item
    let foundItem = false;
    for (const slot of fullDaySchedule) {
      if (currentSeconds >= slot.slotStartSeconds && currentSeconds < slot.slotEndSeconds) {
        // Find current item within the slot
        for (let i = 0; i < slot.items.length; i++) {
          const item = slot.items[i];
          if (currentSeconds >= item.startTimeSeconds && currentSeconds < item.endTimeSeconds) {
            foundItem = true;
            setCurrentPlayingItem({
              ...item,
              slotIndex: fullDaySchedule.indexOf(slot),
              itemIndex: i,
              playlistName: slot.playlistName
            });

            // Check if player is already playing the correct song
            // Don't restart if we're already playing this item
            const isAlreadyPlayingCorrectSong = isAutoMode && 
              currentSong && 
              currentSong.id === item.id &&
              isPlaying;

            // Start playback only if not already playing the correct song
            const scheduleId = `${slot.playlistId}-${slot.slotStart}-${i}`;
            if (!isAlreadyPlayingCorrectSong && (!hasStartedRef.current || currentScheduleIdRef.current !== scheduleId)) {
              // Log previous item's actual duration before starting new one
              finalizePlaybackLog();
              
              hasStartedRef.current = true;
              currentScheduleIdRef.current = scheduleId;
              
              // Start tracking new item
              currentTrackingItemRef.current = item;
              totalUnmutedTimeRef.current = 0;
              lastMuteStateRef.current = isMuted;
              playbackStartTimeRef.current = isMuted ? null : Date.now();
              
              // Calculate seek position within the current song (in-house radio behavior)
              // This makes the player seek to the correct position based on current time
              const seekPosition = currentSeconds - item.startTimeSeconds;
              
              // Start from current item at the correct position
              startAutoPlaylist(slot.playlistItems, i % slot.playlistItems.length, seekPosition);
            } else if (!currentTrackingItemRef.current) {
              // Not tracking anything - start tracking current item
              currentTrackingItemRef.current = item;
              totalUnmutedTimeRef.current = 0;
              lastMuteStateRef.current = isMuted;
              playbackStartTimeRef.current = isMuted ? null : Date.now();
            } else if (currentTrackingItemRef.current && currentTrackingItemRef.current.id !== item.id) {
              // Tracking a different item - finalize and switch
              finalizePlaybackLog();
              currentTrackingItemRef.current = item;
              totalUnmutedTimeRef.current = 0;
              lastMuteStateRef.current = isMuted;
              playbackStartTimeRef.current = isMuted ? null : Date.now();
            }
            break;
          }
        }
        if (foundItem) break;
      }
    }

    // No current schedule - clear playing item
    if (!foundItem) {
      // Log last item's duration before clearing
      finalizePlaybackLog();
      
      setCurrentPlayingItem(null);
      if (hasStartedRef.current) {
        hasStartedRef.current = false;
        currentScheduleIdRef.current = null;
      }
    }
  }, [currentTime, fullDaySchedule, startAutoPlaylist, finalizePlaybackLog, isAutoMode, currentSong, isPlaying, isMuted]);

  const formatSecondsToTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
    return `${displayHours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')} ${period}`;
  };

  const formatTimeShort = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
    return `${displayHours}:${mins.toString().padStart(2, '0')} ${period}`;
  };

  const getCurrentTimeSeconds = () => {
    return currentTime.getHours() * 3600 + currentTime.getMinutes() * 60 + currentTime.getSeconds();
  };

  const isSlotActive = (slot) => {
    const currentSeconds = getCurrentTimeSeconds();
    return currentSeconds >= slot.slotStartSeconds && currentSeconds < slot.slotEndSeconds;
  };

  const isSlotNext = (slot, index) => {
    const currentSeconds = getCurrentTimeSeconds();
    // Find if this is the next slot after current time
    if (currentSeconds >= slot.slotStartSeconds) return false;
    
    // Check if there's no active slot or this is the first upcoming slot
    const activeSlotExists = fullDaySchedule.some(s => isSlotActive(s));
    if (!activeSlotExists) {
      // No active slot, find the first upcoming one
      return fullDaySchedule.findIndex(s => s.slotStartSeconds > currentSeconds) === index;
    }
    
    // Find the active slot index and check if this is the next one
    const activeIndex = fullDaySchedule.findIndex(s => isSlotActive(s));
    return index === activeIndex + 1;
  };

  const isItemPlaying = (item) => {
    const currentSeconds = getCurrentTimeSeconds();
    return currentSeconds >= item.startTimeSeconds && currentSeconds < item.endTimeSeconds;
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className={isLight ? 'text-gray-500' : 'text-slate-400'}>Loading...</div>
      </div>
    );
  }

  // Separate current, next, and other slots
  const currentSlot = fullDaySchedule.find(slot => isSlotActive(slot));
  const nextSlotIndex = currentSlot 
    ? fullDaySchedule.indexOf(currentSlot) + 1 
    : fullDaySchedule.findIndex(s => s.slotStartSeconds > getCurrentTimeSeconds());
  const nextSlot = nextSlotIndex >= 0 && nextSlotIndex < fullDaySchedule.length 
    ? fullDaySchedule[nextSlotIndex] 
    : null;
  const otherSlots = fullDaySchedule.filter(slot => slot !== currentSlot && slot !== nextSlot);

  return (
    <div className={`px-4 py-8 ${isLight ? 'bg-[#faf8f5]' : ''}`} data-testid="dashboard">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Top Row - Logo Left, Time Center, Now Playing Right */}
        <div className="flex items-center justify-between mb-4 mt-8">
          {/* Business Logo - Left */}
          <div className="flex-shrink-0" style={{ width: '280px' }}>
            {selectedBusiness?.logo_url ? (
              <img 
                src={selectedBusiness.logo_url.startsWith('http') ? selectedBusiness.logo_url : `${BACKEND_URL}${selectedBusiness.logo_url}`}
                alt={selectedBusiness.name}
                className={`rounded-2xl object-contain ${isLight ? 'shadow-md' : ''}`}
                style={{ maxWidth: '280px', maxHeight: '140px' }}
              />
            ) : (
              <div 
                className={`flex items-center justify-center ${isLight ? 'text-gray-400' : 'text-slate-500'}`}
                style={{ width: '280px', height: '100px' }}
              >
                <span className="text-xl">No Logo</span>
              </div>
            )}
          </div>

          {/* Current Time - Center */}
          <div className="flex-1 flex justify-center">
            <div className="text-center">
              <div className={`text-4xl font-mono ${isLight ? 'text-[#e94560]' : 'text-rose-400'}`} data-testid="current-time">
                {currentTime.toLocaleTimeString()}
              </div>
              <div className={`text-lg ${isLight ? 'text-gray-500' : 'text-slate-500'}`}>
                {currentTime.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
              </div>
            </div>
          </div>

          {/* Now Playing - Right */}
          <div className="flex-shrink-0">
            <div 
              className={`rounded-2xl p-3 flex flex-col ${
                isLight 
                  ? 'bg-[#fff5f5] border-2 border-[#e94560]/30' 
                  : 'border-2 border-rose-500/50 bg-gradient-to-r from-rose-900/30 to-rose-800/20'
              }`}
              style={{ width: '280px', height: '140px' }}
              data-testid="now-playing-banner"
            >
              {currentPlayingItem ? (
                <>
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isLight ? 'bg-[#e94560]/10' : 'bg-rose-600/20'
                    }`}>
                      {currentPlayingItem.type === 'song' ? (
                        <Music className={`w-4 h-4 ${isLight ? 'text-[#e94560]' : 'text-rose-400'}`} />
                      ) : (
                        <Mic className={`w-4 h-4 ${isLight ? 'text-amber-600' : 'text-amber-400'}`} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1">
                        <div className={`w-1.5 h-1.5 rounded-full animate-pulse ${isLight ? 'bg-[#e94560]' : 'bg-rose-500'}`} />
                        <span className={`text-xs font-medium uppercase ${isLight ? 'text-[#e94560]' : 'text-rose-400'}`}>Now Playing</span>
                      </div>
                      <p className={`text-xs ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>
                        {currentPlayingItem.type === 'song' ? 'Song' : 'Announcement'}
                      </p>
                    </div>
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      <div className={`w-1 h-2 rounded animate-pulse ${isLight ? 'bg-[#e94560]' : 'bg-rose-400'}`} />
                      <div className={`w-1 h-3 rounded animate-pulse ${isLight ? 'bg-[#e94560]' : 'bg-rose-400'}`} style={{ animationDelay: '0.1s' }} />
                      <div className={`w-1 h-2 rounded animate-pulse ${isLight ? 'bg-[#e94560]' : 'bg-rose-400'}`} style={{ animationDelay: '0.2s' }} />
                    </div>
                  </div>
                  
                  <div className="flex-1 flex items-center justify-center">
                    <h2 className={`text-sm font-bold text-center line-clamp-2 ${isLight ? 'text-gray-800' : 'text-white'}`}>{currentPlayingItem.name}</h2>
                  </div>
                  
                  {/* Volume Control */}
                  <div className={`flex items-center gap-2 pt-1 border-t ${isLight ? 'border-[#e94560]/20' : 'border-rose-500/20'}`}>
                    <button 
                      onClick={toggleMute}
                      className={`p-1 rounded-lg transition-colors ${isLight ? 'hover:bg-[#e94560]/10' : 'hover:bg-rose-500/20'}`}
                      data-testid="volume-toggle-btn"
                    >
                      {isMuted ? (
                        <VolumeX className={`w-3 h-3 ${isLight ? 'text-[#e94560]' : 'text-rose-400'}`} />
                      ) : (
                        <Volume2 className={`w-3 h-3 ${isLight ? 'text-[#e94560]' : 'text-rose-400'}`} />
                      )}
                    </button>
                    <Slider
                      value={[isMuted ? 0 : volume]}
                      max={100}
                      step={1}
                      onValueChange={(val) => updateVolume(val[0])}
                      className="flex-1"
                      data-testid="volume-slider"
                    />
                    <span className={`text-xs font-mono w-6 ${isLight ? 'text-[#e94560]' : 'text-rose-400'}`}>{isMuted ? 0 : volume}%</span>
                  </div>
                </>
              ) : (
                <div className={`flex-1 flex flex-col items-center justify-center ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>
                  <Music className="w-8 h-8 mb-1 opacity-50" />
                  <p className="text-sm">Nothing playing</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Full Day Schedule */}
        <div className="space-y-4">
          {fullDaySchedule.length === 0 ? (
            <div className={`rounded-xl border p-8 text-center ${
              isLight 
                ? 'border-[#ebe7e0] bg-white' 
                : 'border-slate-800 bg-slate-900/40'
            }`}>
              <ListMusic className={`w-12 h-12 mx-auto mb-4 ${isLight ? 'text-gray-300' : 'text-slate-600'}`} />
              <p className={`text-lg ${isLight ? 'text-gray-600' : 'text-slate-400'}`}>No playlists scheduled for today</p>
              <p className={`text-sm mt-2 ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>
                Go to Playlists page to create and schedule your music
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Current Slot - Full Detail */}
              {currentSlot && (
                <DetailedSlotCard 
                  slot={currentSlot} 
                  isActive={true}
                  isNext={false}
                  isItemPlaying={isItemPlaying}
                  formatTimeShort={formatTimeShort}
                  formatDuration={formatDuration}
                  currentPlaylist={currentPlaylist}
                  currentIndex={currentIndex}
                  currentPlayingRef={currentPlayingRef}
                  isLight={isLight}
                />
              )}

              {/* Up Next Section - Collapsible, default collapsed */}
              {(nextSlot || otherSlots.length > 0) && (
                <Collapsible defaultOpen={false} className={`rounded-xl border overflow-hidden ${
                  isLight 
                    ? 'border-[#ebe7e0] bg-white' 
                    : 'border-slate-800 bg-slate-900/40'
                }`}>
                  <CollapsibleTrigger className={`w-full p-4 flex items-center justify-between transition-colors ${
                    isLight ? 'hover:bg-gray-50' : 'hover:bg-slate-800/30'
                  }`}>
                    <h3 className={`text-lg font-semibold flex items-center gap-2 ${isLight ? 'text-gray-800' : 'text-white'}`}>
                      <Clock className={`w-5 h-5 ${isLight ? 'text-[#e94560]' : 'text-rose-400'}`} />
                      Up Next & Other Slots ({(nextSlot ? 1 : 0) + otherSlots.length})
                    </h3>
                    <ChevronDown className={`w-5 h-5 ${isLight ? 'text-gray-400' : 'text-slate-400'}`} />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="px-4 pb-4">
                    <div className="space-y-4">
                      {/* Next Slot */}
                      {nextSlot && (
                        <DetailedSlotCard 
                          slot={nextSlot} 
                          isActive={false}
                          isNext={true}
                          isItemPlaying={isItemPlaying}
                          formatTimeShort={formatTimeShort}
                          formatDuration={formatDuration}
                          currentPlaylist={currentPlaylist}
                          currentIndex={currentIndex}
                          isLight={isLight}
                        />
                      )}

                      {/* Other Slots - Compact View */}
                      {otherSlots.length > 0 && (
                        <div className="space-y-2">
                          <h4 className={`text-sm font-medium uppercase tracking-wide ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>Later Today</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {otherSlots.map((slot, index) => (
                              <CompactSlotCard 
                                key={`${slot.playlistId}-${slot.slotStart}`}
                                slot={slot}
                                formatTimeShort={formatTimeShort}
                                isLight={isLight}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Detailed Slot Card Component - Shows full song list
const DetailedSlotCard = ({ slot, isActive, isNext, isItemPlaying, formatTimeShort, formatDuration, currentPlaylist, currentIndex, currentPlayingRef, isLight }) => {
  return (
    <div 
      className={`rounded-xl border-2 transition-all ${
        isActive 
          ? isLight ? 'border-[#e94560]/50 bg-[#e94560]/5' : 'border-violet-500/50 bg-violet-900/20'
          : isNext
          ? isLight ? 'border-[#6b8e8e]/30 bg-[#6b8e8e]/5' : 'border-blue-500/30 bg-blue-900/10'
          : isLight ? 'border-[#ebe7e0] bg-white' : 'border-slate-800 bg-slate-900/40'
      }`}
      data-testid={`schedule-slot-${isActive ? 'current' : 'next'}`}
    >
      {/* Slot Header */}
      <div className={`p-4 border-b ${isActive ? (isLight ? 'border-[#e94560]/20' : 'border-violet-500/30') : isNext ? (isLight ? 'border-[#6b8e8e]/20' : 'border-blue-500/20') : (isLight ? 'border-[#ebe7e0]' : 'border-slate-800')}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isActive && (
              <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
            )}
            {isNext && (
              <ChevronRight className={`w-5 h-5 ${isLight ? 'text-[#6b8e8e]' : 'text-blue-400'}`} />
            )}
            <h3 className={`text-lg font-semibold ${isLight ? 'text-gray-800' : 'text-white'}`}>{slot.playlistName}</h3>
            {isActive && (
              <span className="px-2 py-0.5 bg-emerald-600/20 border border-emerald-600/30 rounded text-xs text-emerald-600 flex items-center gap-1">
                <Lock className="w-3 h-3" />
                NOW PLAYING
              </span>
            )}
            {isNext && (
              <span className={`px-2 py-0.5 rounded text-xs ${isLight ? 'bg-[#6b8e8e]/10 border border-[#6b8e8e]/30 text-[#6b8e8e]' : 'bg-blue-600/20 border border-blue-600/30 text-blue-400'}`}>
                UP NEXT
              </span>
            )}
          </div>
          <div className="text-right">
            <div className={`text-sm font-medium ${isLight ? 'text-gray-600' : 'text-slate-300'}`}>
              {formatTimeShort(slot.slotStartSeconds)} - {formatTimeShort(slot.slotEndSeconds)}
            </div>
            <div className={`text-xs ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>
              {slot.items.length} items • {slot.totalLoops} loop{slot.totalLoops > 1 ? 's' : ''}
            </div>
          </div>
        </div>
      </div>

      {/* Slot Items */}
      <div className="p-4 max-h-64 overflow-y-auto">
        <div className="space-y-1">
          {slot.items.map((item, itemIndex) => {
            const isCurrentItem = isActive && isItemPlaying(item);
            return (
              <div
                key={`${item.id}-${itemIndex}`}
                ref={isCurrentItem && currentPlayingRef ? currentPlayingRef : null}
                className={`flex items-center gap-3 p-2 rounded-lg transition-all ${
                  isCurrentItem
                    ? isLight ? 'bg-[#e94560] text-white' : 'bg-violet-600 text-white'
                    : isLight ? 'hover:bg-gray-50' : 'hover:bg-slate-800/50'
                }`}
                data-testid={`schedule-item-${itemIndex}`}
              >
                <span className={`text-xs font-mono w-6 ${isCurrentItem ? 'text-white/70' : (isLight ? 'text-gray-400' : 'text-slate-600')}`}>
                  {itemIndex + 1}.
                </span>
                
                {item.type === 'song' ? (
                  <Music className={`w-4 h-4 flex-shrink-0 ${isCurrentItem ? 'text-white' : (isLight ? 'text-[#e94560]' : 'text-violet-400')}`} />
                ) : (
                  <Mic className={`w-4 h-4 flex-shrink-0 ${isCurrentItem ? 'text-white' : (isLight ? 'text-[#c4a35a]' : 'text-amber-400')}`} />
                )}

                <div className="flex-1 min-w-0">
                  <span className={`text-sm truncate ${isCurrentItem ? 'text-white font-medium' : (isLight ? 'text-gray-700' : 'text-slate-300')}`}>
                    {item.name}
                    {item.artist && <span className={isCurrentItem ? 'text-white/70' : (isLight ? 'text-gray-400' : 'text-slate-500')}> - {item.artist}</span>}
                  </span>
                  {item.type === 'song' && item.genre && (
                    <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${
                      isCurrentItem ? 'bg-white/20 text-white' : (isLight ? 'bg-gray-100 text-gray-500' : 'bg-slate-700 text-slate-400')
                    }`}>
                      {item.genre}
                    </span>
                  )}
                </div>

                <div className={`text-xs font-mono ${isCurrentItem ? 'text-white/70' : (isLight ? 'text-gray-400' : 'text-slate-500')}`}>
                  {item.startTimeFormatted.split(' ')[0]}
                </div>

                <div className={`text-xs ${isCurrentItem ? 'text-white/70' : (isLight ? 'text-gray-400' : 'text-slate-600')}`}>
                  ({formatDuration(item.duration)})
                </div>

                {item.loopNumber > 1 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                    isCurrentItem ? 'bg-white/20 text-white' : (isLight ? 'bg-gray-100 text-gray-500' : 'bg-slate-800 text-slate-500')
                  }`}>
                    L{item.loopNumber}
                  </span>
                )}

                {isCurrentItem && (
                  <div className="flex items-center gap-0.5">
                    <div className="w-0.5 h-3 bg-white rounded animate-pulse" />
                    <div className="w-0.5 h-4 bg-white rounded animate-pulse" style={{ animationDelay: '0.15s' }} />
                    <div className="w-0.5 h-3 bg-white rounded animate-pulse" style={{ animationDelay: '0.3s' }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Compact Slot Card Component - Shows only playlist name and time
const CompactSlotCard = ({ slot, formatTimeShort, isLight }) => {
  const isPast = slot.slotEndSeconds < (new Date().getHours() * 3600 + new Date().getMinutes() * 60);
  
  return (
    <div 
      className={`rounded-lg border p-3 transition-all ${
        isPast 
          ? isLight ? 'border-[#ebe7e0]/50 bg-gray-50 opacity-60' : 'border-slate-800/50 bg-slate-900/20 opacity-60'
          : isLight ? 'border-[#ebe7e0] bg-white hover:bg-gray-50' : 'border-slate-700 bg-slate-800/30 hover:bg-slate-800/50'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Music className={`w-4 h-4 ${isPast ? (isLight ? 'text-gray-300' : 'text-slate-600') : (isLight ? 'text-[#e94560]' : 'text-violet-400')}`} />
          <span className={`font-medium truncate ${isPast ? (isLight ? 'text-gray-400' : 'text-slate-500') : (isLight ? 'text-gray-800' : 'text-white')}`}>
            {slot.playlistName}
          </span>
        </div>
      </div>
      <div className={`text-xs mt-1 ${isPast ? (isLight ? 'text-gray-300' : 'text-slate-600') : (isLight ? 'text-gray-500' : 'text-slate-400')}`}>
        {formatTimeShort(slot.slotStartSeconds)} - {formatTimeShort(slot.slotEndSeconds)}
        <span className={`ml-2 ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>
          ({slot.playlistItems.length} items)
        </span>
      </div>
    </div>
  );
};

export default Dashboard;
